import * as React from 'react'

import { Link } from '@ministries/storybook'
import classnames from 'classnames'
import errorStack from 'error-stack-parser'
import { useMatches } from 'react-router'

import { useI18n } from '@app/utilities/i18n/provider'

function RedBox({ error }: { error: Error }) {
  const [isVisible, setIsVisible] = React.useState(true)
  const frames = errorStack.parse(error)

  return (
    <div
      className={classnames(
        'fixed inset-0 z-10 flex items-center justify-center transition',
        {
          'pointer-events-none opacity-0': !isVisible
        }
      )}
    >
      <button
        type="button"
        className="absolute inset-0 block h-full w-full bg-black opacity-75"
        onClick={() => setIsVisible(false)}
      />
      <div className="relative mx-[5vw] my-16 max-h-[75vh] overflow-y-auto rounded-lg border border-red-300 bg-red-500 p-12 text-white">
        <h2 className="text-2xl font-semibold">{error.message}</h2>
        <div>
          {frames.map(frame => (
            <div
              key={[frame.fileName, frame.lineNumber, frame.columnNumber].join(
                '-'
              )}
              className="pt-4"
            >
              <div className="pt-2">{frame.functionName}</div>
              <div className="font-mono opacity-75">
                {frame.fileName}:{frame.lineNumber}:{frame.columnNumber}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

type HeroSectionProps = {
  title: string | React.ReactNode
  subtitle?: string | React.ReactNode
  action?: React.ReactNode
}

function HeroSection({ title, subtitle, action }: HeroSectionProps) {
  return (
    <div className="flex flex-auto flex-col">
      <h2 className="text-3xl font-semibold tracking-tight">{title}</h2>
      {subtitle ? (
        <p className="mt-3 text-base text-zinc-700">{subtitle}</p>
      ) : null}
      {action ? (
        <div className="mt-14 flex flex-col space-y-4">{action}</div>
      ) : null}
    </div>
  )
}

export function ErrorPage({
  error,
  heroProps
}: {
  error?: Error
  heroProps: HeroSectionProps
}) {
  const resolvedHeroProps: HeroSectionProps = heroProps
  const { t } = useI18n()

  return (
    <>
      <noscript>
        <div className="bg-black p-8 text-white">
          <h1 className="text-3xl font-semibold">{heroProps.title}</h1>
          <p className="mt-2 text-2xl">{heroProps.subtitle}</p>
          <small>{t('error.noscript')}</small>
        </div>
      </noscript>
      <main className="relative">
        {error && import.meta.env.MODE === 'development' ? (
          <RedBox error={error} />
        ) : null}
        <HeroSection {...resolvedHeroProps} />
      </main>
    </>
  )
}

export function NotFoundPage() {
  const { t } = useI18n()
  const matches = useMatches()
  const last = matches[matches.length - 1]
  const pathnames = last?.pathname

  return (
    <div>
      <ErrorPage
        heroProps={{
          title: t('error.404.title'),
          subtitle: t('error.404.subtitle', {
            pathname: pathnames ?? '/'
          }),
          action: <Link href="/">{t('error.go_home')}</Link>
        }}
      />
    </div>
  )
}

export function ServerError({ error }: { error?: Error }) {
  const { t } = useI18n()
  const matches = useMatches()
  const last = matches[matches.length - 1]
  const pathname = last?.pathname

  return (
    <ErrorPage
      error={error}
      heroProps={{
        title: t('error.500.title'),
        subtitle: t('error.500.subtitle', {
          pathname: pathname ?? '/'
        }),
        action: <Link href="/">{t('error.go_home')}</Link>
      }}
    />
  )
}
